import React, { useState, useEffect } from 'react';
import { MessageCircle, Reply, Heart, Flag, Edit2, Trash2, User } from 'lucide-react';
import { commentService, realtimeService } from '../../../services/outageService';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';

const UserComments = ({ outageId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (outageId) {
      loadComments();
      
      // Set up real-time subscription for comments
      const channel = realtimeService?.subscribeToOutageComments(outageId, (payload) => {
        console.log('Real-time comment update:', payload);
        if (payload?.eventType === 'INSERT') {
          loadComments(); // Reload to get proper user data
        } else if (payload?.eventType === 'UPDATE') {
          setComments(prev => prev?.map(comment => 
            comment?.id === payload?.new?.id ? { ...comment, ...payload?.new } : comment
          ));
        } else if (payload?.eventType === 'DELETE') {
          setComments(prev => prev?.filter(comment => comment?.id !== payload?.old?.id));
        }
      });

      return () => {
        realtimeService?.unsubscribe(channel);
      };
    }
  }, [outageId]);

  const loadComments = async () => {
    if (!outageId) return;
    
    setLoading(true);
    const { data, error } = await commentService?.getCommentsForOutage(outageId);
    
    if (error) {
      setError(error?.message);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  };

  const handleSubmitComment = async (e) => {
    e?.preventDefault();
    if (!newComment?.trim() || !user) return;

    setSubmitting(true);
    const { data, error } = await commentService?.addComment(
      outageId, 
      newComment?.trim(), 
      replyTo?.id || null
    );

    if (error) {
      setError(error?.message);
    } else {
      setNewComment('');
      setReplyTo(null);
      loadComments(); // Reload to get the new comment with user data
    }
    setSubmitting(false);
  };

  const handleEditComment = async (commentId) => {
    if (!editContent?.trim()) return;

    const { data, error } = await commentService?.updateComment(commentId, editContent?.trim());

    if (error) {
      setError(error?.message);
    } else {
      setEditingComment(null);
      setEditContent('');
      loadComments();
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    const { error } = await commentService?.deleteComment(commentId);

    if (error) {
      setError(error?.message);
    } else {
      loadComments();
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setNewComment(`@${comment?.user?.full_name || 'User'} `);
  };

  const handleEdit = (comment) => {
    setEditingComment(comment?.id);
    setEditContent(comment?.content);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const renderComment = (comment, isReply = false) => {
    const isOwner = user?.id === comment?.user_id;
    const isEditing = editingComment === comment?.id;

    return (
      <div key={comment?.id} className={`${isReply ? 'ml-12' : ''} border-b border-gray-100 last:border-b-0`}>
        <div className="py-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gray-500" />
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-gray-900">
                  {comment?.user?.full_name || 'Anonymous User'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(comment?.created_at)}
                </span>
                {comment?.is_solution && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Solution
                  </span>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e?.target?.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Edit your comment..."
                  />
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      onClick={() => handleEditComment(comment?.id)}
                      disabled={!editContent?.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingComment(null);
                        setEditContent('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mb-2">
                    {comment?.content}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-xs">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Heart className="w-3 h-3" />
                      <span>{comment?.upvote_count || 0}</span>
                    </div>
                    
                    {user && !isReply && (
                      <button
                        onClick={() => handleReply(comment)}
                        className="flex items-center space-x-1 text-gray-500 hover:text-blue-600"
                      >
                        <Reply className="w-3 h-3" />
                        <span>Reply</span>
                      </button>
                    )}
                    
                    {isOwner && (
                      <>
                        <button
                          onClick={() => handleEdit(comment)}
                          className="flex items-center space-x-1 text-gray-500 hover:text-blue-600"
                        >
                          <Edit2 className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteComment(comment?.id)}
                          className="flex items-center space-x-1 text-gray-500 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Delete</span>
                        </button>
                      </>
                    )}
                    
                    {!isOwner && (
                      <button className="flex items-center space-x-1 text-gray-500 hover:text-red-600">
                        <Flag className="w-3 h-3" />
                        <span>Report</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Render replies */}
          {comment?.replies?.map(reply => renderComment(reply, true))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <MessageCircle className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Comments</h2>
        </div>
        <div className="space-y-4">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <MessageCircle className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">
          Comments ({comments?.length || 0})
        </h2>
      </div>
      {error && (
        <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}
      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="mb-6">
          {replyTo && (
            <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
              <span className="text-blue-700">
                Replying to {replyTo?.user?.full_name || 'Anonymous User'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setReplyTo(null);
                  setNewComment('');
                }}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                Cancel
              </button>
            </div>
          )}
          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gray-500" />
              </div>
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e?.target?.value)}
                placeholder={replyTo ? "Write a reply..." : "Share your experience or updates..."}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="submit"
                  disabled={!newComment?.trim() || submitting}
                  size="sm"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md text-center">
          <p className="text-gray-600">
            <a href="/user-login" className="text-blue-600 hover:text-blue-800">
              Sign in
            </a>{' '}
            to join the discussion
          </p>
        </div>
      )}
      {/* Comments List */}
      <div className="space-y-0">
        {comments?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>No comments yet</p>
            <p className="text-sm">Be the first to share your experience</p>
          </div>
        ) : (
          comments?.map(comment => renderComment(comment))
        )}
      </div>
    </div>
  );
};

export default UserComments;